import { useState, useEffect } from "react";
import { getCompanies } from "../services/companyService";
import useDebounce from "./useDebounce";

export interface Bank {
  id: string;
  name: string;
}

interface RawCompany {
  id: string;
  name: string;
}

const PAGE_SIZE = 10;

const useGetBanks = () => {
  const [options, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState("");
  const [loadParams, setLoadParams] = useState({ search: "", page: 1 });

  const debouncedSearch = useDebounce(search, 400);

  useEffect(() => {
    setLoadParams({ search: debouncedSearch, page: 1 });
  }, [debouncedSearch]);

  useEffect(() => {
    let cancelled = false;

    const fetchBanks = async () => {
      setLoading(true);
      const { search: s, page } = loadParams;
      const params: Record<string, unknown> = { page, limit: PAGE_SIZE };
      if (s.trim()) params.q = s.trim();
      const role = localStorage.getItem("role");
      if (role === "lender") {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        if (user?.id) params.lenderId = user.id;
      }

      const { data, error } = (await getCompanies(params)) as {
        data: { data?: RawCompany[]; pagination?: { total: number } } | null;
        error: unknown;
      };

      if (cancelled) return;

      if (!error) {
        const list: RawCompany[] = Array.isArray(data?.data) ? data!.data! : [];
        const normalized: Bank[] = list.map((c) => ({
          id: c.id,
          name: c.name,
        }));
        const total = data?.pagination?.total ?? 0;
        setBanks((prev) => {
          const updated = page === 1 ? normalized : [...prev, ...normalized];
          setHasMore(updated.length < total);
          return updated;
        });
      }
      setLoading(false);
    };

    fetchBanks();
    return () => {
      cancelled = true;
    };
  }, [loadParams]);

  return {
    options,
    loading,
    hasMore,
    onSearch: (value: string) => setSearch(value),
    onLoadMore: () =>
      setLoadParams((prev) => ({ ...prev, page: prev.page + 1 })),
  };
};

export default useGetBanks;
